/**
 * OnsiteGuidedWalkthrough.js — 6-step pass/fail walkthrough for on-site staff
 * (property manager · leasing agent · lead maintenance · field contractor).
 * FAIL routes to scoped work order (auto_dispatch_on_fail).
 */
import React, { useEffect, useState } from "react";
import CooIdentityRibbon from "./CooIdentityRibbon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const btnPass = { padding: "6px 14px", background: CYAN, color: "#020A1F", border: 0, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" };
const btnPassGhost = { padding: "6px 14px", background: "transparent", color: INK, border: `1px solid ${CYAN}88`, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" };
const btnFail = { padding: "6px 14px", background: "transparent", color: INK, border: "1px solid #f04040", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" };

export default function OnsiteGuidedWalkthrough({ role, propertyAddress }) {
  const [guide, setGuide] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [notes, setNotes] = useState({});
  const [uploads, setUploads] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitOk, setSubmitOk] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/role-dashboard/onsite?role=${encodeURIComponent(role || "property_manager")}`)
      .then(r => r.json()).then(setGuide);
  }, [role]);

  if (!guide) return null;

  const total = guide.steps.length;
  const done = Object.keys(statuses).length;
  const failed = Object.values(statuses).filter(v => v === "fail").length;
  const addressReady = (propertyAddress || "").trim().length > 3;

  const captureUpload = (stepId) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setUploads(u => ({ ...u, [stepId]: reader.result }));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!addressReady) {
      setSubmitStatus("Enter the property address above before sealing.");
      setSubmitOk(false);
      return;
    }
    setSubmitStatus("Sealing guided walkthrough → dispatching scoped work orders…");
    const findings = guide.steps.map(s => ({
      id: s.id,
      zone: s.zone,
      verdict: statuses[s.id] || "skipped",
      note: notes[s.id] || "",
      photo_b64: uploads[s.id] || null,
    }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/role-dashboard/onsite/seal`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role, property_address: propertyAddress, findings }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Seal failed");
      setSubmitOk(true);
      setSubmitStatus(`✓ Walkthrough ${d.walkthrough_id} sealed. ${d.work_orders_dispatched} scoped work order(s) dispatched. ${d.evidence_count} evidence photo(s) attached.`);
    } catch (e) {
      setSubmitOk(false);
      setSubmitStatus(`✗ Seal failed: ${e.message}`);
    }
  };

  return (
    <div style={card} data-testid="onsite-guided-walkthrough">
      <CooIdentityRibbon />
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
        COO-GUIDED WALKTHROUGH · {role?.toUpperCase()}
      </div>
      <div style={{ fontSize: 12, color: CHROME, marginBottom: 12 }}>{guide.instructions}</div>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        Progress: <span style={{ color: CYAN }}>{done}</span> / {total} · Fails routed: <span style={{ color: failed ? "#f04040" : CYAN }}>{failed}</span>
      </div>
      {guide.steps.map((s, idx) => {
        const verdict = statuses[s.id];
        return (
          <div key={s.id} style={{ padding: "12px 0", borderTop: `1px solid ${CHROME}22` }} data-testid={`onsite-step-${s.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: CYAN, letterSpacing: "0.1em" }}>{String(idx + 1).padStart(2, "0")} · {s.zone.toUpperCase()}</div>
                <div style={{ fontSize: 13 }}>{s.prompt}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={verdict === "pass" ? btnPass : btnPassGhost}
                  onClick={() => setStatuses(v => ({ ...v, [s.id]: "pass" }))}
                  data-testid={`onsite-${s.id}-pass`}>PASS</button>
                <button style={{ ...btnFail, background: verdict === "fail" ? "#f04040" : "transparent" }}
                  onClick={() => setStatuses(v => ({ ...v, [s.id]: "fail" }))}
                  data-testid={`onsite-${s.id}-fail`}>FAIL</button>
              </div>
            </div>
            {verdict === "fail" && (
              <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                <input placeholder="What is the failure?"
                  value={notes[s.id] || ""}
                  onChange={e => setNotes(n => ({ ...n, [s.id]: e.target.value }))}
                  data-testid={`onsite-${s.id}-note`}
                  style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "8px 10px", borderRadius: 6, fontSize: 13 }} />
                <label style={{ fontSize: 11, color: CHROME }}>
                  Clock-stop upload (evidence photo · optional):
                  <input type="file" accept="image/*" capture="environment"
                    onChange={captureUpload(s.id)}
                    data-testid={`onsite-${s.id}-upload`}
                    style={{ display: "block", marginTop: 4, fontSize: 11 }} />
                </label>
                {uploads[s.id] && <img src={uploads[s.id]} alt="evidence" style={{ maxWidth: 160, borderRadius: 6, border: `1px solid ${CHROME}44` }} />}
                <div style={{ fontSize: 11, color: "#f5a942" }}>
                  ⚑ Auto-dispatch: this FAIL will route direct to the owner ledger as a scoped work order.
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ marginTop: 14 }}>
        <button style={{ ...btnPass, padding: "10px 20px", fontSize: 13, opacity: (done === 0 || !addressReady) ? 0.5 : 1 }}
          disabled={done === 0 || !addressReady}
          onClick={submit}
          data-testid="onsite-seal-btn">
          Seal walkthrough → dispatch scoped work orders
        </button>
        {!addressReady && (
          <div style={{ marginTop: 8, color: "#f5a942", fontSize: 12 }} data-testid="onsite-address-required">
            ⚑ Enter the property address in the intake form above before sealing.
          </div>
        )}
        {submitStatus && (
          <div style={{ marginTop: 8, color: submitOk ? CYAN : "#f5a942", fontSize: 12 }} data-testid="onsite-seal-status">
            {submitStatus}
          </div>
        )}
      </div>
    </div>
  );
}
